import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient, queryClient } from '@/lib';
import type { ExperienceLabel, LabelType } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceEventsQueryKey } from '@/utils/queryKeys';
import type {
  DeleteExperienceLabelMutation,
  DeleteExperienceLabelMutationVariables,
} from './useDeleteLabel.generated';
import { useLabelCache } from './useLabelCache';

const query = gql`
  mutation DeleteExperienceLabel($input: DeleteExperienceLabelInput!) {
    deleteExperienceLabel(input: $input)
  }
`;

export const useDeleteLabel = (labelType: LabelType) => {
  const experience = useEffectiveExperience();
  const cache = useLabelCache();

  const { mutate } = useMutation<
    DeleteExperienceLabelMutation,
    QueryError,
    DeleteExperienceLabelMutationVariables,
    ExperienceLabel | undefined
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        DeleteExperienceLabelMutation,
        DeleteExperienceLabelMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      const label = cache.getLabel(labelType, input.labelId);
      cache.removeLabel(labelType, input.labelId);
      return label;
    },
    onSuccess: () => {
      // Deleting a label clears it from any events that referenced it.
      queryClient.invalidateQueries({
        queryKey: createExperienceEventsQueryKey(experience.id),
      });
    },
    onError: (_error, _variables, label) => {
      if (label) {
        cache.addLabel(labelType, label.id, {
          text: label.text,
          sortOrder: label.sortOrder,
        });
      }
    },
  });

  const removeLabel = useCallback(
    (
      labelId: string,
      options?: MutateOptions<
        DeleteExperienceLabelMutation,
        QueryError,
        DeleteExperienceLabelMutationVariables,
        ExperienceLabel | undefined
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            labelId,
          },
        },
        options
      ),
    [mutate, experience.id]
  );

  return { removeLabel };
};
